/**
 * FloatingNavBar Component
 * Modern navigation bar that renders from menuItems array with dynamic icons
 */

import React from 'react';
import * as Icons from '@ant-design/icons';
import { NavButton } from './NavButton';
import type { FloatingMenuItem } from '@/api/types/tenants.types';
import './FloatingNavBar.css';

interface FloatingNavBarProps {
  /** Menu items from API */
  menuItems: FloatingMenuItem[];
  /** Toggle handler */
  onToggle: (code: string) => void;
}

/**
 * Map icon name from API to Ant Design icon component
 */
const getIconComponent = (iconName: string): React.ReactNode => {
  // Map API icon names to Ant Design icon names (PascalCase + Outlined)
  const iconMap: Record<string, string> = {
    menu: 'MenuOutlined',
    message: 'MessageOutlined',
    thunderbolt: 'ThunderboltOutlined',
    calendar: 'CalendarOutlined',
    team: 'TeamOutlined',
    user: 'UserOutlined',
    setting: 'SettingOutlined',
    home: 'HomeOutlined',
    mail: 'MailOutlined',
    bell: 'BellOutlined',
    search: 'SearchOutlined',
    plus: 'PlusOutlined',
    edit: 'EditOutlined',
    delete: 'DeleteOutlined',
    check: 'CheckOutlined',
    close: 'CloseOutlined',
    info: 'InfoCircleOutlined',
    warning: 'WarningOutlined',
    question: 'QuestionCircleOutlined',
    star: 'StarOutlined',
    heart: 'HeartOutlined',
    like: 'LikeOutlined',
    comment: 'CommentOutlined',
    share: 'ShareAltOutlined',
    download: 'DownloadOutlined',
    upload: 'UploadOutlined',
    folder: 'FolderOutlined',
    file: 'FileOutlined',
    picture: 'PictureOutlined',
    video: 'VideoCameraOutlined',
    audio: 'AudioOutlined',
    link: 'LinkOutlined',
    lock: 'LockOutlined',
    unlock: 'UnlockOutlined',
    eye: 'EyeOutlined',
    cloud: 'CloudOutlined',
    database: 'DatabaseOutlined',
    api: 'ApiOutlined',
    code: 'CodeOutlined',
    bug: 'BugOutlined',
    robot: 'RobotOutlined',
    experiment: 'ExperimentOutlined',
  };

  const antIconName =
    iconMap[iconName.toLowerCase()] || 'QuestionCircleOutlined';
  const IconComponent = (
    Icons as unknown as Record<string, React.ComponentType>
  )[antIconName];

  if (IconComponent) {
    return <IconComponent />;
  }

  // Fallback to question icon
  return <Icons.QuestionCircleOutlined />;
};

/**
 * Get label for menu item based on code
 */
const getLabel = (code: string): string => {
  const labelMap: Record<string, string> = {
    SIDE_MENU: 'Side menu',
    AI_CHAT: 'AI chat',
    BLOCKSTREAM: 'Blockstream',
    MOJ_KALENDAR: 'Moj kalendar',
    CONTACTS: 'Contacts',
  };
  return labelMap[code] || code;
};

/**
 * Get tooltip for menu item based on code
 */
const getTooltip = (code: string): string => {
  const tooltipMap: Record<string, string> = {
    SIDE_MENU: 'Toggle Side Menu',
    AI_CHAT: 'Open AI Assistant',
    BLOCKSTREAM: 'Open Activity Stream',
    MOJ_KALENDAR: 'Open My Calendar',
    CONTACTS: 'Open Contacts',
  };
  return tooltipMap[code] || `Open ${code}`;
};

export const FloatingNavBar: React.FC<FloatingNavBarProps> = ({
  menuItems,
  onToggle,
}) => {
  return (
    <div className="floating-nav-bar">
      {menuItems.map((item) => (
        <NavButton
          key={item.code}
          icon={getIconComponent(item.icon)}
          label={getLabel(item.code)}
          active={item.status}
          onClick={() => onToggle(item.code)}
          tooltip={getTooltip(item.code)}
        />
      ))}
    </div>
  );
};
